import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store';

Vue.use(VueRouter);

// meta.auth is required to tell the system whether a route need authentication or not.
// When meta.auth set to true, meta.scope (array/string) is required to determine the permission.
//
// To avoid setting meta.auth/scope on every children we want to set meta.auth at the parent
// Then we can overwrite it on the children (when necessary)

const routes = [
  {
    path: '',
    component: () => import('../views/main/Index.vue'),
    meta: {
      auth: true,
      scope: ['org_admin', 'admin', 'user'],
    },
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('../views/main/Home.vue'),
      },
      {
        path: 'view/:productId-:routeId-:productName-:routeName',
        name: 'View',
        component: () => import('../views/main/Tableau.vue'),
        props: true,
      },
      {
        path: 'user',
        name: 'UserProfile',
        component: () => import('../views/main/User.vue'),
      },
      {
        path: 'user/:userId',
        name: 'UserProfileId',
        component: () => import('../views/main/User.vue'),
        props: true,
      },
      {
        path: 'usernew',
        name: 'NewUserOrg',
        component: () => import('../views/main/NewUserOrg.vue'),
      },
      {
        path: 'org',
        name: 'OrgProfile',
        component: () => import('../views/main/Org.vue'),
        meta: {
          scope: ['org_admin', 'admin'],
        },
      },
      {
        path: 'contact',
        name: 'Contact',
        component: () => import('../views/main/info/Contact.vue'),
      },
      {
        path: 'disclaimer',
        name: 'Disclaimer',
        component: () => import('../views/main/info/Disclaimer.vue'),
      },
      {
        path: 'faq',
        name: 'FAQ',
        component: () => import('../views/main/info/FAQ.vue'),
      },
      {
        path: 'imprint',
        name: 'Imprint',
        component: () => import('../views/main/info/Imprint.vue'),
      },
      {
        path: 'search',
        name: 'SearchResult',
        component: () => import('@/views/main/SearchResult.vue'),
        props: (route) => ({ productName: route.query.productName, tag: route.query.tag }),
      },
      /* {
        path: '404-2',
        name: '404',
        component: () => import('../views/404.vue'),
      }, */
    ],
  },
  {
    path: '',
    component: () => import('../views/auth/Index.vue'),
    meta: {
      auth: false,
    },
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('../views/auth/Login.vue'),
      },
      {
        path: 'password-request',
        name: 'PasswordRequest',
        component: () => import('../views/auth/PasswordRequest.vue'),
      },
      {
        path: 'password-reset',
        name: 'PasswordReset',
        component: () => import('../views/auth/PasswordReset.vue'),
      },
      {
        path: 'signup',
        name: 'Signup',
        component: () => import('../views/auth/Signup.vue'),
      },
      {
        path: 'password',
        name: 'Password',
        component: () => import('../views/auth/PasswordChange.vue'),
        meta: {
          auth: true,
          scope: ['org_admin', 'admin', 'user'],
        },
      },
    ],
  },
  {
    path: '/admin',
    component: () => import('../views/admin/Index.vue'),
    meta: {
      auth: true,
      scope: 'admin',
    },
    children: [
      {
        path: '',
        redirect: { name: 'AllOrgs' },
      },
      {
        path: 'orgs',
        name: 'AllOrgs',
        component: () => import('../views/admin/org/AllOrgs.vue'),
      },
      {
        path: 'orgs/new',
        name: 'NewOrg',
        component: () => import('../views/admin/org/NewOrg.vue'),
        props: true,
      },
      {
        path: 'orgs/:orgId',
        name: 'ViewOrg',
        component: () => import('../views/admin/org/ViewOrg.vue'),
        props: true,
      },
      {
        path: 'products',
        name: 'AllProducts',
        component: () => import('../views/admin/product/AllProducts.vue'),
      },
      {
        path: 'products/new',
        name: 'NewProduct',
        component: () => import('@/views/admin/product/NewProduct.vue'),
      },
      {
        path: 'products/:productId',
        name: 'ViewProduct',
        component: () => import('../views/admin/product/ViewProduct.vue'),
        props: true,
      },
      {
        path: 'product/manage',
        name: 'ManageProduct',
        component: () => import('@/views/admin/product/ManageProduct.vue'),
      },
      {
        path: 'product/manage/:productId',
        name: 'ManageProductById',
        component: () => import('@/views/admin/product/ManageProduct.vue'),
        props: true,
      },
      {
        path: 'users',
        name: 'AllUsers',
        component: () => import('../views/admin/user/AllUsers.vue'),
      },
      {
        path: 'users/new',
        name: 'NewUser',
        component: () => import('../views/admin/user/NewUser.vue'),
        props: true,
      },
      {
        path: 'users/:userId',
        name: 'ViewUser',
        component: () => import('../views/admin/user/ViewUser.vue'),
        props: true,
      },
      {
        path: 'news',
        name: 'AllNews',
        component: () => import('@/views/admin/news/AllNews.vue'),
      },
      {
        path: 'news/new',
        name: 'NewNews',
        component: () => import('@/views/admin/news/NewNews.vue'),
      },
      {
        path: 'news/:newsId',
        name: 'ViewNews',
        props: true,
        component: () => import('@/views/admin/news/ViewNews.vue'),
      },
      {
        path: 'tags',
        name: 'AllTags',
        component: () => import('@/views/admin/product/AllTags.vue'),
      },
      {
        path: 'tags/:tagId',
        name: 'ViewTag',
        props: true,
        component: () => import('@/views/admin/product/ViewTag.vue'),
      },
    ],
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../views/404.vue'),
    meta: { auth: false },
  },
  {
    path: '/403',
    name: '403',
    component: () => import('../views/403.vue'),
    meta: { auth: false },
  },
  {
    path: '*',
    redirect: { name: '404' },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // linkActiveClass: 'is-active',
  linkExactActiveClass: 'is-active',
});

router.beforeEach((to, from, next) => {
  function proceed() {
    // Prioritize children's meta over the parent's
    const theMeta = to.meta.auth !== undefined ? to.meta : to.matched[0].meta;
    const authRequired = theMeta.auth;
    const loggedIn = store.getters['auth/loggedIn'];
    const permitted = !authRequired ? true : store.getters['auth/isPermited'](theMeta.scope);

    if (authRequired && !loggedIn) {
      console.log('[router] not authenticated -> login');
      next({
        name: 'Login',
        query: { redirect: to.fullPath },
      });
    } else if (authRequired && loggedIn && !permitted) {
      console.log('[router] authenticated but not permitted -> 403');
      next({ name: '403' });
    } else {
      // console.log('[router] public / authenticated and permitted');
      next();
    }
  }

  store.dispatch('auth/PRE_LOGIN');

  if (!store.state.auth.userloaded) {
    console.log('[router] waiting for store to be initialized ...');
    store.watch(
      (state) => state.auth.userloaded,
      (value) => {
        console.debug('[router] ok store initialization state changed', value);
        if (value === true) {
          proceed();
        }
      },
    );
  } else {
    proceed();
  }
});

export default router;
